import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, forkJoin, map, of, switchMap, Subject } from 'rxjs';
import { UserService } from '@services/user/user.service';
import { Building, ManagingCompany } from '@app/shared/models/building.interface';
import { BuildingWithUsersDTO } from '@app/shared/models/buildingWithUsersDTO';
import { ManagingCompanyUsersTableComponent } from './managing-company-users-table/managing-company-users-table.component';
import { ManagingCompanyUsersFiltersComponent } from '@app/managing-company-administration/managing-company-users/filters/managing-company-users-filters.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ToastService } from '@services/toast/toast.service';
import { ManagingCompanyService } from '@services/managing-company.service';
import { User } from '@app/shared/models/user.interface';
import { UserRole } from '@app/shared/models/user-role.interface';
import { UserRoleService } from '@services/user/user-role.service';
import { AddOrUpdateExistingUserPayload } from '@app/shared/models/AddOrUpdateExistingUserPayload';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent, ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { ActivatedRoute } from '@angular/router';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';
import { take, takeUntil } from 'rxjs/operators';
import {
  ManagingCompanyUserEditDialogComponent
} from '@app/managing-company-administration/managing-company-users/managing-company-users-table/managing-company-user-edit-dialog/managing-company-user-edit-dialog.component';
import { UpdateManagingCompanyUserPayload } from '@app/shared/models/update-managing-company-user.interface';

@Component({
  selector: 'app-managing-company-users',
  standalone: true,
  imports: [CommonModule, ManagingCompanyUsersTableComponent, ManagingCompanyUsersFiltersComponent, MatProgressSpinner],
  templateUrl: './managing-company-users.component.html',
  styleUrl: './managing-company-users.component.scss'
})
export class ManagingCompanyUsersComponent implements OnInit, OnDestroy {
  public allBuildingUsers: BuildingWithUsersDTO[] = [];
  public filteredBuildingUsers: BuildingWithUsersDTO[] = [];
  public buildings: Building[] = [];
  public userSearchQuery: string = '';
  public selectedBuildingIds: number[] = [];
  public managingCompany: ManagingCompany | null = null;
  public loading: boolean = true;
  public hasUnassigned: boolean = false;
  public allMcBuildings: Building[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private toastService: ToastService,
    private managingCompanyService: ManagingCompanyService,
    private userRoleService: UserRoleService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loading = true;

    this.allBuildingUsers = [];
    this.filteredBuildingUsers = [];
    this.buildings = [];
    this.allMcBuildings = [];
    this.hasUnassigned = false;

    combineLatest([
      this.userService.getCurrentUser().pipe(take(1)),
      this.activatedRoute.paramMap.pipe(
        map((params) => {
          const raw = params.get('managingCompanyId');
          const parsed = Number(raw);
          return Number.isFinite(parsed) ? parsed : null;
        })
      )
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([currentUser, mcIdFromUrl]) => {
          const managingCompanyId = mcIdFromUrl ?? currentUser.managingCompanyIds?.[0] ?? null;
          return this.ensureManagingCompany(currentUser, managingCompanyId);
        }),
        switchMap(({ user: currentUser, managingCompanyId }) => {
          if (!currentUser || !managingCompanyId) {
            return of({ user: null as User | null, users: [] as User[], allMcBuildings: [] as Building[] });
          }

          return forkJoin({
            user: of(currentUser),
            users: this.userService.getUsersByManagingCompany(managingCompanyId),
            allMcBuildings: this.loadAllBuildingsForManagingCompany(managingCompanyId)
          });
        }),
        switchMap(({ user, users, allMcBuildings }) => this.loadUsersTable(user, users, allMcBuildings))
      )
      .subscribe({
        next: ({ buildingsWithUsers, buildingsForFilter, allMcBuildings, hasUnassigned }) => {
          this.allBuildingUsers = buildingsWithUsers;
          this.filteredBuildingUsers = buildingsWithUsers;
          this.buildings = buildingsForFilter || [];
          this.allMcBuildings = allMcBuildings || [];
          this.hasUnassigned = hasUnassigned;
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private ensureManagingCompany(currentUser: User, managingCompanyId: number | null) {
    if (!managingCompanyId) {
      return of({ user: null as User | null, managingCompanyId: null as number | null });
    }

    const hasAdminAuthorityForCompany = (currentUser.managingCompanyAuthorities ?? []).some(
      (mca) =>
        mca.managingCompanyId === managingCompanyId &&
        (mca.authorities ?? []).includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN)
    );

    if (!hasAdminAuthorityForCompany) {
      return of({ user: null as User | null, managingCompanyId: null as number | null });
    }

    return this.managingCompanyService.getManagingCompanies().pipe(
      map((companies) => {
        this.managingCompany = (companies ?? []).find((mc) => mc.id === managingCompanyId) || null;
        this.changeDetectorRef.markForCheck();
        return { user: currentUser, managingCompanyId };
      })
    );
  }

  private loadAllBuildingsForManagingCompany(managingCompanyId: number) {
    return this.userService
      .getBuildings()
      .pipe(
        map((allBuildings) =>
          (allBuildings ?? []).filter((building) => building.managingCompanyId === managingCompanyId)
        )
      );
  }

  private loadUsersTable(currentUser: User, companyUsers: User[], mcBuildings: Building[]) {
    if (!currentUser) {
      return of({
        buildingsWithUsers: [] as BuildingWithUsersDTO[],
        buildingsForFilter: [] as Building[],
        allMcBuildings: [] as Building[],
        hasUnassigned: false
      });
    }

    const { buildingsWithUsers, buildingsForFilter } = this.groupUsersByBuilding(companyUsers, mcBuildings);

    const allowedIds = new Set(mcBuildings.map((b) => b.id));
    const unassigned = this.findUnassignedUsers(companyUsers, allowedIds);

    if (unassigned.length) {
      buildingsWithUsers.push({ building: null as any, users: unassigned });
    }

    return of(this.setUsersTableView(buildingsWithUsers, buildingsForFilter, mcBuildings, unassigned));
  }

  private groupUsersByBuilding(companyUsers: User[], mcBuildings: Building[]) {
    const buildingsWithUsers: BuildingWithUsersDTO[] = [];
    const buildingsForFilter: Building[] = [];

    for (const building of mcBuildings) {
      const usersForThisBuilding = companyUsers.filter((user) =>
        (user.buildingAuthorizations || []).some((auth) => {
          const id = (auth?.buildingId ?? auth?.building?.id) as number | undefined;
          return id === building.id;
        })
      );

      if (usersForThisBuilding.length > 0) {
        buildingsWithUsers.push({ building, users: usersForThisBuilding });
        buildingsForFilter.push(building);
      }
    }

    return { buildingsWithUsers, buildingsForFilter };
  }

  private findUnassignedUsers(companyUsers: User[], allowedBuildingIds: Set<number>) {
    return companyUsers.filter((user) => {
      const authorizations = user.buildingAuthorizations || [];
      if (authorizations.length === 0) return true;

      return !authorizations.some((authorization) => {
        const authorizedBuildingId = (authorization?.buildingId ?? authorization?.building?.id) as number | undefined;
        return authorizedBuildingId != null && allowedBuildingIds.has(authorizedBuildingId);
      });
    });
  }

  private setUsersTableView(
    buildingsWithUsers: BuildingWithUsersDTO[],
    buildingsForFilter: Building[],
    allMcBuildings: Building[],
    unassigned: User[]
  ) {
    return {
      buildingsWithUsers,
      buildingsForFilter,
      allMcBuildings,
      hasUnassigned: unassigned.length > 0
    };
  }

  onBuildingsSelected(buildingIds: number[]): void {
    this.selectedBuildingIds = buildingIds;
    this.applyCombinedFilter();
  }

  onUserSearchChanged(query: string): void {
    this.userSearchQuery = query;
    this.applyCombinedFilter();
  }

  applyCombinedFilter(): void {
    let filtered = this.allBuildingUsers;

    if (this.buildings.length > 0 || this.hasUnassigned) {
      if (this.selectedBuildingIds.length > 0) {
        filtered = filtered.filter((buildingUserDto) => {
          if (!buildingUserDto.building) return this.selectedBuildingIds.includes(0);
          return this.selectedBuildingIds.includes(buildingUserDto.building.id);
        });
      } else {
        filtered = [];
      }
    }

    const searchLower = this.userSearchQuery.trim().toLowerCase();
    if (searchLower) {
      filtered = filtered
        .map((buildingUserDto) => ({
          building: buildingUserDto.building,
          users: (buildingUserDto.users || []).filter(
            (user) =>
              user.name?.toLowerCase().includes(searchLower) || user.emailAddress?.toLowerCase().includes(searchLower)
          )
        }))
        .filter((buildingUserDto) => (buildingUserDto.users?.length || 0) > 0);
    }

    this.filteredBuildingUsers = filtered;
    this.changeDetectorRef.markForCheck();
  }

  onAddFromExistingUsers(payload: AddOrUpdateExistingUserPayload): void {
    const newUserRole: UserRole = {
      userId: payload.user.id,
      roleIds: [payload.roleId]
    };

    if (this.managingCompany && !payload.user.managingCompanyIds.includes(this.managingCompany.id)) {
      this.userService.updateManagingCompanyUser(payload.user.id, this.managingCompany.id, null).subscribe({
        next: () => {
          payload.user.managingCompanyIds.push(this.managingCompany.id);
          this.updateUserRolesForBuilding(payload.buildingId, newUserRole);
        },
        error: () => {
          this.toastService.error({
            message: 'Failed to assign managing company.',
            dataCy: 'failed-to-assign-mc'
          });
        }
      });
    } else {
      this.updateUserRolesForBuilding(payload.buildingId, newUserRole);
    }
  }

  private updateUserRolesForBuilding(buildingId: number, userRole: UserRole): void {
    this.userRoleService.updateUserRoles(buildingId, userRole).subscribe({
      next: () => {
        this.toastService.success({
          message: 'User assigned successfully.',
          dataCy: 'user-assigned-success'
        });
        this.loadData();
      },
      error: () => {
        this.toastService.error({
          message: 'Failed to update roles.',
          dataCy: 'failed-to-update-roles'
        });
      }
    });
  }

  onDeleteUserFromBuilding({ buildingId, userId }: { buildingId: number; userId: number }) {
    if (buildingId == null) {
      this.onDeleteUserFromBuildingRemoveFromManagingCompany(userId);
    } else {
      this.onDeleteUserFromBuildingRemoveFromBuilding(userId, buildingId);
    }
  }

  private onDeleteUserFromBuildingRemoveFromManagingCompany(userId: number) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: new ConfirmDialogData(
        'Are you sure you want to remove this user from your managing company?',
        'Remove user from managing company'
      )
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userService.removeManagingCompanyFromUser(userId, this.managingCompany?.id as number).subscribe({
          next: () => {
            this.refreshData();
            this.toastService.success({
              message: 'User removed from managing company.',
              dataCy: 'user-removed-success'
            });
          },
          error: (err) => {
            console.error(err);
            this.toastService.error({
              message: 'Failed to remove user. Please try again',
              dataCy: 'user-removed-error'
            });
          }
        });
      }
    });
  }

  private onDeleteUserFromBuildingRemoveFromBuilding(userId: number, buildingId: number) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: new ConfirmDialogData(
        'This user will no longer have access to this building. Are you sure?',
        'Remove user from building'
      )
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userService.removeUser(buildingId, userId).subscribe({
          next: () => {
            this.refreshData();
            this.toastService.success({
              message: 'User roles removed from building.',
              dataCy: 'user-role-removed-success'
            });
          },
          error: (err) => {
            console.error(err);
            this.toastService.error({
              message: 'Failed to remove roles. Please try again.',
              dataCy: 'user-role-removed-error'
            });
          }
        });
      }
    });
  }

  private refreshData(): void {
    this.loadData();
    this.onBuildingsSelected(this.selectedBuildingIds);
    this.onUserSearchChanged(this.userSearchQuery);
  }

  onEditUser(editedUser: User): void {
    this.userService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((currentUser) => {
        const dialogRef = this.dialog.open(ManagingCompanyUserEditDialogComponent, {
          width: '600px',
          data: {
            editedUser: {
              ...editedUser,
              managingCompanyAuthorities: [...(editedUser.managingCompanyAuthorities ?? [])]
            },
            currentUser,
            managingCompanyId: this.managingCompany?.id
          }
        });

        dialogRef.afterClosed().subscribe((updatedUser: User | undefined) => {
          if (!updatedUser || !this.managingCompany?.id) {
            return;
          }

          const currentManagingCompanyAuthorities =
            updatedUser.managingCompanyAuthorities?.find((mc) => mc.managingCompanyId === this.managingCompany!.id)
              ?.authorities ?? [];

          const managingCompanyAuthoritiesPayload: UpdateManagingCompanyUserPayload = {
            managingCompanyAuthorities: [
              {
                managingCompanyId: this.managingCompany!.id,
                authorities: currentManagingCompanyAuthorities
              }
            ]
          };

          this.userService
            .updateManagingCompanyUser(updatedUser.id, this.managingCompany.id, managingCompanyAuthoritiesPayload)
            .subscribe({
              next: () => {
                this.toastService.success({
                  message: 'User managing company authorities updated successfully.',
                  dataCy: 'user-managing-company-authorities-updated-success'
                });
                this.refreshData();
              },
              error: (err) => {
                console.error(err);
                this.toastService.error({
                  message: 'Failed to update user managing company authorities.',
                  dataCy: 'user-managing-company-authorities-updated-error'
                });
              }
            });
        });
      });
  }

  protected readonly ManagingCompanyAuthorityType = ManagingCompanyAuthorityType;
}
